import { useState } from 'react';
import { formatTemporal, parseTemporal, temporalPlaceholder, type TemporalType } from '@/shared/utils/temporal';

export type TemporalDraftProps = {
    temporalDraft?: string | null;
    onTemporalDraftChange?: (draft: string | null) => void;
};

/** Invalid text has its own state; the domain value is always canonical. */
export function useTemporalInput(type: TemporalType, value: string | null | undefined,
    onChange: ((value: string | null) => void) | undefined, props: TemporalDraftProps, locale: string) {
    const [editingText, setEditingText] = useState<string | null>(null);
    const [localDraft, setLocalDraft] = useState<string | null>(null);
    const draft = props.onTemporalDraftChange ? props.temporalDraft : localDraft;
    const setDraft = (text: string | null) => {
        setLocalDraft(text);
        props.onTemporalDraftChange?.(text);
    };
    const commit = (next: string | null) => {
        setEditingText(null);
        setDraft(null);
        if (next !== value) onChange?.(next);
    };
    return {
        locale, commit,
        inputProps: {
            value: editingText ?? draft ?? formatTemporal(value, type, locale),
            onFocus: () => {
                setEditingText(draft ?? formatTemporal(value, type, locale));
            },
            onBlur: () => setEditingText(null),
            placeholder: temporalPlaceholder(type, locale),
            onChangeText: (text: string) => {
                setEditingText(text);
                let next = parseTemporal(text, type, locale);
                // Minute-only controls must not erase seconds already stored by the application.
                const previous = parseTemporal(value, type);
                if (next && previous && type !== 'date' && !/:\d{2}:\d{2}$/.test(text)) next = next.slice(0, -2) + previous.slice(-2);
                if (next === undefined) setDraft(text);
                else {
                    setDraft(null);
                    if (next !== value) onChange?.(next);
                }
            },
        },
    };
}
